/**
 * Building blocks of the phone lists (SAM-944, mocks M1 to M3): cards instead of tables, filter chips and a pager
 * with 44 px targets. Tailwind + tokens (styling freeze, SAM-175).
 */
import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

export interface PhoneChip {
  id: string;
  label: string;
}

export const PhoneChips: React.FC<{ chips: PhoneChip[]; active: string; onPick: (id: string) => void }> = ({ chips, active, onPick }) => (
  <div className="flex gap-2 overflow-x-auto" role="group" aria-label="Filter">
    {chips.map((c) => {
      const on = c.id === active;
      return (
        <button
          key={c.id}
          type="button"
          aria-pressed={on}
          onClick={() => onPick(c.id)}
          className={`h-9 shrink-0 whitespace-nowrap rounded-full border border-solid px-3.5 text-[13px] font-semibold ${
            on
              ? 'border-[var(--tenant-primary)] bg-[var(--tenant-primary)] text-white'
              : 'border-[var(--line)] bg-white text-[var(--ink-700)]'
          }`}
        >
          {c.label}
        </button>
      );
    })}
  </div>
);

export type PillTone = 'ok' | 'muted' | 'warn';
const PILL: Record<PillTone, string> = {
  ok: 'bg-[var(--ok-bg)] text-[#047857]',
  muted: 'bg-[var(--line-subtle)] text-[#4B5563]',
  warn: 'bg-[var(--warn-bg)] text-[var(--warn-fg)]',
};

export const PhonePill: React.FC<{ tone: PillTone; children: React.ReactNode }> = ({ tone, children }) => (
  <span className={`inline-flex shrink-0 rounded-full px-2.5 py-[3px] text-xs font-semibold ${PILL[tone]}`}>{children}</span>
);

export const Initials: React.FC<{ text: string; warn?: boolean }> = ({ text, warn }) => (
  <span
    aria-hidden
    className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-bold ${
      warn ? 'bg-[var(--warn-bg)] text-[var(--warn-fg)]' : 'bg-[#E7F0EE] text-[var(--tenant-primary)]'
    }`}
  >
    {text}
  </span>
);

export const cardClass = 'rounded-lg bg-white shadow-[var(--sh-1)]';

export const initialsOf = (name: string): string => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  return ((parts[0][0] ?? '') + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
};

export const PhonePager: React.FC<{ page: number; limit: number; total: number; onPage: (page: number) => void }> = ({
  page, limit, total, onPage,
}) => {
  if (total <= 0) return null;
  const from = (page - 1) * limit + 1;
  const to = Math.min(page * limit, total);
  const last = Math.ceil(total / limit);
  const btn = 'flex h-11 w-11 items-center justify-center rounded-lg border border-solid border-[var(--line)] bg-white disabled:text-[var(--ink-400)]';
  return (
    <div className="flex items-center justify-between pt-1 text-[13px] text-[var(--ink-500)]">
      <span>{`${from.toLocaleString('en-US')}–${to.toLocaleString('en-US')} of ${total.toLocaleString('en-US')}`}</span>
      <div className="flex gap-2">
        <button type="button" aria-label="Previous page" disabled={page <= 1} onClick={() => onPage(page - 1)} className={btn}>
          <ChevronLeft size={16} aria-hidden />
        </button>
        <button type="button" aria-label="Next page" disabled={page >= last} onClick={() => onPage(page + 1)} className={btn}>
          <ChevronRight size={16} aria-hidden />
        </button>
      </div>
    </div>
  );
};

/** Loading, error (with retry) and empty states shared by the phone lists. */
export const PhoneListState: React.FC<{
  loading: boolean;
  failed: boolean;
  empty: boolean;
  emptyText: string;
  onRetry?: () => void;
}> = ({ loading, failed, empty, emptyText, onRetry }) => {
  if (loading) return <div role="status" aria-busy="true" className="py-10 text-center text-sm text-[var(--ink-500)]">Loading…</div>;
  if (failed) {
    return (
      <div role="alert" className="rounded-lg border border-solid border-[#FECACA] bg-[var(--bad-bg)] px-3.5 py-3 text-sm text-[var(--bad-fg)]">
        Could not load the list.{' '}
        {onRetry && <button type="button" className="btn" onClick={onRetry}>Try again</button>}
      </div>
    );
  }
  if (empty) return <div role="status" className="py-10 text-center text-sm text-[var(--ink-500)]">{emptyText}</div>;
  return null;
};
